import React, { Component } from 'react'
import { getMessage } from 'lib/translator'
import { Dialog } from 'components/Popup'
import API from 'lib/api'
import moment from 'moment'
import { Input, MultiTextInput } from 'components/Form'
import Loader from 'components/Loader'
import './style.scss'
import mockRewardData from './mockRewardData'

const serverErrorMsg = getMessage('offer.scanRewards.form.error')

class ScanRewardDetailsModal extends Component {
  constructor(props) {
    super(props)
    this.fetchRewardData = this.fetchRewardData.bind(this)
    this.state = {
      errorDialog: null,
      dataLoaded: false,
      values: null,
    }
  }

  componentDidMount() {
    const { rewardId, campaignId, customerId } = this.props
    this.fetchRewardData({ rewardId, campaignId, customerId })
  }

  componentDidUpdate(prevProps) {
    // Refetch data if rewardId changes
    if (prevProps.rewardId !== this.props.rewardId) {
      const { rewardId, campaignId, customerId } = this.props
      this.setState({ dataLoaded: false })
      this.fetchRewardData({ rewardId, campaignId, customerId })
    }
  }

  async fetchRewardData({ rewardId, campaignId, customerId }) {
    if (rewardId) {
      try {
        // Build URL with filter parameters
        const queryParams = new URLSearchParams()
        if (campaignId) {
          queryParams.append('campaign_id', campaignId)
        }
        if (customerId) {
          queryParams.append('customer_id', customerId)
        }

        const queryString = queryParams.toString()
        const url = queryString
          ? `/scan-campaign-service/campaigns/rewards?${queryString}`
          : `/scan-campaign-service/campaigns/rewards`

        const api = new API({
          url,
        })
        const res = await api.get()

        // The API returns { data: { customerReward: [...] } }
        const rewardsArray = res?.data?.customerReward || []

        // Filter the response array to find the reward with matching ID
        const rewardData = rewardsArray.find(
          (item) => String(item.id) === String(rewardId)
        )

        if (rewardData) {
          this.setState({
            values: rewardData,
            dataLoaded: true,
            errorDialog: null,
          })
        } else {
          // If reward not found in filtered results, use mock data
          const mockData = this.getMockDataById(rewardId)
          this.setState({
            values: mockData,
            dataLoaded: true,
            errorDialog: mockData
              ? null
              : `Reward with ID ${rewardId} not found in filtered results`,
          })
        }
      } catch (error) {
        console.error(error)
        // Use mock data if API fails
        const mockData = this.getMockDataById(rewardId)
        this.setState({
          values: mockData,
          dataLoaded: true,
          errorDialog: mockData ? null : error?.message || serverErrorMsg,
        })
      }
    } else {
      // If no rewardId, use first mock data entry
      const mockData = mockRewardData?.data?.customerReward?.[0] || {}
      this.setState({
        values: mockData,
        dataLoaded: true,
      })
    }
  }

  getMockDataById(rewardId) {
    // Find reward in mock data by ID
    const reward = mockRewardData?.data?.customerReward?.find(
      (item) => String(item.id) === String(rewardId)
    )
    return reward || mockRewardData?.data?.customerReward?.[0] || {}
  }

  render() {
    if (!this.state.dataLoaded) {
      return <Loader />
    }

    const { values } = this.state
    if (!values) {
      return null
    }

    const reason = values?.reason || {}
    const issuedAt = values?.issuedAt
      ? moment(values.issuedAt).format('D MMM YYYY hh:mm A')
      : values?.issued_at
        ? moment(values.issued_at).format('D MMM YYYY hh:mm A')
        : ''
    const newAccount =
      reason?.newAccount !== undefined ? String(reason.newAccount) : ''
    const firstScanOfDay =
      reason?.firstScanOfDay !== undefined ? String(reason.firstScanOfDay) : ''
    const nthCustomerOfDay = reason?.nthCustomerOfDay || ''
    const customerSegmentsNames = reason?.customerSegments
      ? reason.customerSegments.map((seg) => seg.name)
      : []
    const storeId = reason?.storeId || values?.storeId || ''
    const offerId = values?.offerId || reason?.reward?.rewardValue || ''
    const promoCode = values?.promoCode || reason?.promoCode || ''
    const ruleId =
      reason?.rule?.id || reason?.reward?.ruleId || values?.ruleId || ''
    const ruleTypeRaw =
      reason?.rule?.type || reason?.reward?.type || values?.ruleType || ''
    const ruleTypeMap = {
      1: 'Generic',
      2: 'GoldenTicket',
      3: 'Segment',
    }
    const ruleType = ruleTypeRaw ? ruleTypeMap[ruleTypeRaw] || ruleTypeRaw : ''
    const sequence = reason?.rule?.policy?.sequences || values?.sequence || ''
    const firstEverScan =
      reason?.rule?.policy?.firstEverScan !== undefined
        ? String(reason.rule.policy.firstEverScan)
        : ''

    return (
      <>
        <form className="scan-reward-form" onSubmit={(e) => e.preventDefault()}>
          <div className="customer-information-section">
            <h3 className="section-heading">Customer Information</h3>
            <div className="inputs-row-container">
              <Input
                type="text"
                name="newAccount"
                className="new-account-input"
                label="New Account"
                readOnly
                value={newAccount}
              />
              <Input
                type="text"
                name="firstScanOfDay"
                className="first-scan-of-day-input"
                label="First Scan Of The Day"
                readOnly
                value={firstScanOfDay}
              />
            </div>
            <div className="inputs-row-container">
              <Input
                type="text"
                name="nthCustomerOfDay"
                className="nth-customer-of-day-input"
                label="Nth Customer Of Day"
                readOnly
                value={nthCustomerOfDay}
              />
              <Input
                type="text"
                name="issuedAt"
                className="issued-at-input"
                label="Issued At"
                readOnly
                value={issuedAt}
              />
            </div>
            <div className="inputs-row-container">
              <Input
                type="text"
                name="storeId"
                className="store-id-input"
                label="Store ID"
                readOnly
                value={storeId}
              />
            </div>
          </div>

          <div className="reward-section">
            <h3 className="section-heading">Reward</h3>
            <div className="inputs-row-container">
              <Input
                type="text"
                name="offerId"
                className="offer-id-input"
                label="Offer ID"
                readOnly
                value={offerId}
              />
              <Input
                type="text"
                name="promoCode"
                className="promo-code-input"
                label="Promo Code"
                readOnly
                value={promoCode}
              />
            </div>
          </div>

          <div className="eligible-rule-section">
            <h3 className="section-heading">Eligible Rule</h3>
            <div className="inputs-row-container">
              <Input
                type="text"
                name="ruleId"
                className="rule-id-input"
                label="Rule ID"
                readOnly
                value={ruleId}
              />
              <Input
                type="text"
                name="ruleType"
                className="rule-type-input"
                label="Rule Type"
                readOnly
                value={ruleType}
              />
            </div>
            <div className="policy-subsection">
              <h4 className="subsection-heading">Policy</h4>
              <div className="inputs-row-container">
                <Input
                  type="text"
                  name="firstEverScan"
                  className="first-ever-scan-input"
                  label="First Ever Scan"
                  readOnly
                  value={firstEverScan}
                />
                <Input
                  type="text"
                  name="sequence"
                  className="sequence-input"
                  label="Sequence"
                  readOnly
                  value={sequence}
                />
              </div>
            </div>
          </div>

          <div className="customer-segments-section">
            <h3 className="section-heading">Customer Segments</h3>
            <div className="inputs-row-container customer-segments-input">
              <MultiTextInput
                name="customerSegments"
                label=""
                readOnly
                value={customerSegmentsNames}
                placeholder=""
              />
            </div>
          </div>
        </form>

        {this.state.errorDialog && (
          <Dialog
            className="error-dialog"
            show={!!this.state.errorDialog}
            title="Error"
            information={this.state.errorDialog}
            close={() => this.setState({ errorDialog: null })}
            closeText={getMessage('dialog.okText')}
          />
        )}
      </>
    )
  }
}

export default ScanRewardDetailsModal
